<template>
  <div id="app">
    <v-app id="inspire" :class="{ 'app-impersonating': shouldShowBanner }">
      <template v-if="authorized">
        <ImpersonationBanner />
        <Toolbars />
        <v-main class="back-color">
          <!-- no-gutters : sans v-container, les marges négatives de v-row débordaient de 24 px (scroll horizontal) -->
          <v-row no-gutters>
            <v-col class="full-height">
              <keep-alive include="Inscriptions,MaterialManagement">
                <router-view />
              </keep-alive>
            </v-col>
          </v-row>
        </v-main>
        <NavigationDrawerLeft />
      </template>
      <template v-else>
        <Unauthorized />
      </template>
      <!-- SpotlightSearch modal global -->
      <SpotlightSearch />
    </v-app>
    <!-- Les toasts sont maintenant gérés par vue-toastification -->
  </div>
</template>

<script>
  import Vue from 'vue';
  import ImpersonationBanner from '@/components/ImpersonationBanner.vue';
  import NavigationDrawerLeft from '@/components/NavigationDrawerLeft.vue';
  import SpotlightSearch from '@/components/SpotlightSearch';
  import Toolbars from '@/components/Toolbars.vue';
  import Unauthorized from '@/components/Unauthorized';

  export default {
    name: 'App',
    components: {
      Toolbars,
      NavigationDrawerLeft,
      Unauthorized,
      SpotlightSearch,
      ImpersonationBanner,
    },
    data () {
      return {
        authorized: false,
      };
    },
    computed: {
      currentApp() {
        return this.$store.getters.currentApp;
      },
      shouldShowBanner() {
        const adminId = this.$store.getters.hublotAdminId
        const clientId = this.$store.getters.currentClientIdInToken
        const isImpersonating = adminId && clientId && String(adminId) !== String(clientId)
        const noAccount = this.$store.getters.isHublotAdmin && !this.$store.getters.hasCurrentAccount
        return isImpersonating || noAccount
      }
    },
    watch: {
      // Recharger les clients Notion quand on change d'app (carnot/hublot)
      currentApp(newApp, oldApp) {
        if (newApp !== oldApp && this.authorized) {
          this.$store.dispatch('loadNotionClients', { forceRefresh: true })
            .catch((error) => {
              console.error('Impossible de recharger les clients Notion:', error);
            });
        }
      }
    },
    mounted () {
      this.checkAuthorization();
      this.initializeApp();
    },
    methods: {
      checkAuthorization () {
        this.authorized = Vue.prototype.$keycloak.hasRealmRole('hublot_admin')
      },
      initializeApp() {
        if (this.authorized) {
          this.$store.dispatch('loadNotionClients')
            .catch((error) => {
              console.error('Impossible de charger les clients Notion au démarrage:', error);
            });
        }
      },
    },
  };
</script>

<style lang="scss">
// Fond de l'application sur le token de surface (plus de gris #eeeeee)
.v-application {
  background-color: var(--surface) !important;
}

/* Haut du contenu sous les barres fixes : jeton --content-top, lu aussi par les pages
   qui calent leur hauteur sur l'écran (chat). */
.v-application {
  --content-top: 62px; /* Barre d'outils (56px) + marge (6px) */
}
.app-impersonating {
  --content-top: 104px; /* Bannière (40px) + barre d'outils (56px) + marge (8px) */
}

.app-impersonating .v-app-bar {
  top: 40px !important;
}

.app-impersonating .v-navigation-drawer {
  top: 40px !important;
  height: calc(100% - 40px) !important;
}

/* Overlays (dialogs, menus, sheets) : tout descend sous la banniere */
.app-impersonating .v-overlay--active,
.app-impersonating .v-dialog__content {
  top: 40px !important;
  height: calc(100% - 40px) !important;
}

/* Les dialogues plein ecran (ex : editeur SQL) sont eux-memes en position fixed */
.app-impersonating .v-dialog--fullscreen {
  top: 40px !important;
  height: calc(100% - 40px) !important;
  max-height: calc(100% - 40px) !important;
}

/* Contenus cales sur 100vh a l'interieur d'un dialogue plein ecran */
.app-impersonating .v-dialog--fullscreen > .v-card,
.app-impersonating .v-dialog--fullscreen .dialog-card {
  height: calc(100vh - 40px) !important;
  max-height: calc(100vh - 40px) !important;
}

.back-color {
  background-color: var(--surface);
  /* La barre du haut est `app` : v-main reçoit déjà ses 56px de padding-top, on n'ajoute que le reste */
  margin-top: calc(var(--content-top) - 56px);
  /* Pas de min-height: 100% : v-main remplit déjà la hauteur (flex), et 100% + la marge
     ajoutaient 70 px de scroll vertical sur les pages courtes */
}

// Pas de text-align: center hérité : chaque page aligne son contenu à gauche comme le design system
#app {
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--ink);
}

.full-height {
  height: 100%;
}

// Sous 960 px (mobile Vuetify) : même barre d'outils de 56 px, sans rangée d'actions
@media (max-width: 959.98px) {
  .v-application {
    --content-top: 62px;
  }
  .app-impersonating {
    --content-top: 104px; // Bannière (40px) + barre d'outils (56px) + marge (8px)
  }
}

// Téléphone : même gouttière que hb-page pour les pages encore montées dans un v-container
@media (max-width: 599.98px) {
  .v-main .container {
    padding: var(--space-4);
  }
}

// Dialogs arrondis globalement (le défilement de Vuetify, overflow-y: auto, coupe aussi les coins) ;
// le focus visible vient du design system
.v-dialog {
  border-radius: var(--radius-lg) !important;
}

// Scroll global amélioré
html {
  scroll-behavior: smooth;
  overflow-y: auto; // Vuetify 2 force overflow-y: scroll (barre toujours visible, même sans contenu à faire défiler)
}

/* Barres de défilement fines dans les deux sens, piste invisible */
* {
  scrollbar-width: thin;
  scrollbar-color: var(--line-strong) transparent;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: var(--line-strong);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--ink-faint);
}
</style>
<style lang="scss">
// Curseur main sur tous les boutons et icônes cliquables au survol
.v-btn:hover,
.v-icon:hover {
  cursor: pointer !important;
}
</style>
